/**
 * The cart, as the cashier's screen holds it.
 *
 * The arithmetic is deliberately NOT reimplemented here: `cartTotals` on the
 * server is the same shape, and the till shows what the server will post. What
 * lives in this file is only the cart's *behaviour* — the rules §5c and §5e
 * give for how a cart changes when somebody taps a tile.
 */

export interface CartLine {
  itemId: string;
  name: string;
  unitPrice: string;
  qty: number;
  lineDiscount: string;
  /**
   * MD-03's tracking facts for this product, SNAPSHOTTED when the tile was
   * tapped. The catalogue query is filtered by whatever is in the scan box,
   * so the product a line was built from is routinely not in the current
   * result — a cart that looked its own tracking up would forget what it must
   * ask for the moment the cashier typed the next item's name.
   */
  tracking: 'none' | 'serial' | 'batch';
  /** MD-03 field 7: this tile resolves into its PARTS when it sells (R7). */
  autoDeduct: boolean;
  components: readonly {
    componentItemId: string;
    componentName: string;
    tracking: 'none' | 'serial' | 'batch';
    baseQty: string;
  }[];
  /** What has been scanned, keyed as `tracking-capture` keys its blocks. */
  identifiers: Record<string, string>;
}

/** The catalogue facts a tile carries into the cart. */
export interface CartProduct {
  itemId: string;
  name: string;
  price: string;
  tracking: 'none' | 'serial' | 'batch';
  autoDeduct: boolean;
  components: CartLine['components'];
}

/**
 * §5c: "Tapping it adds one to the cart. Tapping again RAISES THE QUANTITY
 * rather than adding a second line." §15 repeats it for the scanner:
 * "Duplicate scan. Raises the quantity rather than adding a line, which is
 * correct."
 */
export function addToCart(cart: readonly CartLine[], product: CartProduct): CartLine[] {
  const at = cart.findIndex((line) => line.itemId === product.itemId);
  if (at === -1) {
    return [
      ...cart,
      {
        itemId: product.itemId,
        name: product.name,
        unitPrice: product.price,
        qty: 1,
        lineDiscount: '0',
        tracking: product.tracking,
        autoDeduct: product.autoDeduct,
        components: product.components,
        identifiers: {},
      },
    ];
  }
  /* A QUANTITY, not an amount: how many of the item are in the cart. The
     line's money (unitPrice, lineDiscount) is untouched here and is only ever
     combined in till-math. */
  // eslint-disable-next-line money/no-raw-money-arithmetic -- item count, not an amount
  return cart.map((line, index) => (index === at ? { ...line, qty: line.qty + 1 } : line));
}

/**
 * §15: "The quantity box accepts typed text; a negative must be refused."
 * Dropping to zero removes the line, which is what the − button does when it
 * runs out of quantity.
 */
export function setQty(cart: readonly CartLine[], itemId: string, qty: number): CartLine[] {
  if (!Number.isFinite(qty) || qty < 0) return [...cart];
  if (qty === 0) return cart.filter((line) => line.itemId !== itemId);
  return cart.map((line) => (line.itemId === itemId ? { ...line, qty } : line));
}

export function removeLine(cart: readonly CartLine[], itemId: string): CartLine[] {
  return cart.filter((line) => line.itemId !== itemId);
}

export function setLineDiscount(cart: readonly CartLine[], itemId: string, discount: string): CartLine[] {
  return cart.map((line) => (line.itemId === itemId ? { ...line, lineDiscount: discount } : line));
}

/**
 * §5c's search order, applied to what the server already filtered: barcode
 * first, then an exact code, then any part of the name. The server orders the
 * same way; this is what lets a scanner's Enter add the top hit without the
 * cashier reading the list.
 */
export function bestMatch<T extends { barcode: string | null; code: string; name: string }>(
  products: readonly T[],
  term: string,
): T | undefined {
  const needle = term.trim();
  if (!needle) return undefined;
  return (
    products.find((p) => p.barcode === needle) ??
    products.find((p) => p.code.toUpperCase() === needle.toUpperCase()) ??
    products.find((p) => p.name.toLowerCase().includes(needle.toLowerCase()))
  );
}
